import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search, Archive, Trash2, AlertTriangle, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (n) => `R ${(parseFloat(n) || 0).toFixed(2)}`;
const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
};

export const UnusedProductsCleanup = ({ open, onClose, onProductsChanged }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("__all__");
  const [months, setMonths] = useState(3);
  const [includeWithStock, setIncludeWithStock] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [archiving, setArchiving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePhrase, setDeletePhrase] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/api/suppliers`)
      .then((res) => setSuppliers(res.data || []))
      .catch(() => setSuppliers([]));
  }, [open]);

  const runScan = async () => {
    setScanning(true);
    setResults(null);
    setSelectedIds(new Set());
    try {
      const params = new URLSearchParams();
      if (supplierId && supplierId !== "__all__") params.set("supplier_id", supplierId);
      params.set("months", String(months));
      params.set("include_with_stock", includeWithStock ? "true" : "false");
      const res = await axios.post(`${API}/api/products/admin/scan-unused?${params.toString()}`);
      setResults(res.data);
      if ((res.data.count || 0) === 0) {
        toast.info("No unused products found for that scope");
      } else {
        toast.success(`Found ${res.data.count} unused product(s) — review before archiving`);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const toggleAll = () => {
    if (!results) return;
    if (selectedIds.size === results.products.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(results.products.map((p) => p.id)));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const exportCsv = () => {
    if (!results || !results.products.length) return;
    const cols = [
      "PLU", "Name", "Supplier", "Category", "Current Stock",
      "Stock Value", "Last Sold", "Last Received", "Created",
    ];
    const rows = results.products.map((p) => [
      p.plu_code || "",
      (p.name || "").replace(/"/g, '""'),
      p.supplier_name || "",
      p.category || "",
      p.current_stock,
      p.stock_value,
      formatDate(p.last_sold_at),
      formatDate(p.last_received_at),
      formatDate(p.created_at),
    ]);
    const csv = [cols, ...rows]
      .map((r) => r.map((v) => `"${v ?? ""}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `unused-products-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const archiveSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(
      `Archive ${selectedIds.size} product(s)? They will be hidden from POS, Stock Master, ` +
      `ordering, and labels. You can restore them later.`
    )) return;
    setArchiving(true);
    try {
      const res = await axios.post(`${API}/api/products/admin/bulk-archive`, {
        product_ids: Array.from(selectedIds),
      });
      toast.success(`${res.data.archived} product(s) archived`);
      // Remove archived rows from the result list
      setResults((prev) => ({
        ...prev,
        products: prev.products.filter((p) => !selectedIds.has(p.id)),
        count: prev.products.filter((p) => !selectedIds.has(p.id)).length,
      }));
      setSelectedIds(new Set());
      onProductsChanged && onProductsChanged();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Archive failed");
    } finally {
      setArchiving(false);
    }
  };

  const hardDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/api/products/admin/bulk-delete`, {
        product_ids: Array.from(selectedIds),
        confirm_phrase: deletePhrase,
      });
      toast.success(`${res.data.deleted} product(s) permanently deleted`);
      setResults((prev) => ({
        ...prev,
        products: prev.products.filter((p) => !selectedIds.has(p.id)),
        count: prev.products.filter((p) => !selectedIds.has(p.id)).length,
      }));
      setSelectedIds(new Set());
      setShowDeleteConfirm(false);
      setDeletePhrase("");
      onProductsChanged && onProductsChanged();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const selectedStockValue = results
    ? results.products
        .filter((p) => selectedIds.has(p.id))
        .reduce((s, p) => s + (p.stock_value || 0), 0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-amber-600" />
            Cleanup Unused Products
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="min-w-[240px]">
              <Label className="text-xs">Supplier</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="cleanup-supplier-select" className="mt-1">
                  <SelectValue placeholder="All suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All suppliers</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">No activity for</Label>
              <Select value={String(months)} onValueChange={(v) => setMonths(parseFloat(v))}>
                <SelectTrigger data-testid="cleanup-months-select" className="mt-1 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 month</SelectItem>
                  <SelectItem value="2">2 months</SelectItem>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                  <SelectItem value="18">18 months</SelectItem>
                  <SelectItem value="24">24 months</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
              <Checkbox
                checked={includeWithStock}
                onCheckedChange={(v) => setIncludeWithStock(!!v)}
                data-testid="cleanup-include-stock-checkbox"
              />
              Include products with stock on hand
            </label>

            <Button
              onClick={runScan}
              disabled={scanning}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="cleanup-scan-btn"
            >
              {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Scan
            </Button>
          </div>

          {/* Results */}
          {results && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm">
                  <strong>{results.count}</strong> unused product(s) found
                  {results.count > 0 && (
                    <span className="text-gray-500 ml-2">
                      • Total stock value: {formatCurrency(results.total_stock_value)}
                    </span>
                  )}
                  {selectedIds.size > 0 && (
                    <span className="text-amber-700 ml-2 font-medium">
                      • {selectedIds.size} selected ({formatCurrency(selectedStockValue)})
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={exportCsv} disabled={!results.count}>
                    <Download className="h-4 w-4 mr-1" />
                    CSV
                  </Button>
                  <Button
                    onClick={archiveSelected}
                    disabled={selectedIds.size === 0 || archiving}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="cleanup-archive-btn"
                  >
                    {archiving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Archive className="h-4 w-4 mr-1" />}
                    Archive ({selectedIds.size})
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={selectedIds.size === 0}
                    data-testid="cleanup-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete forever
                  </Button>
                </div>
              </div>

              {results.count > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[50vh] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="p-2 w-8 text-left">
                            <Checkbox
                              checked={selectedIds.size > 0 && selectedIds.size === results.products.length}
                              onCheckedChange={toggleAll}
                              data-testid="cleanup-select-all-checkbox"
                            />
                          </th>
                          <th className="p-2 text-left">PLU</th>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">Supplier</th>
                          <th className="p-2 text-right">Stock</th>
                          <th className="p-2 text-right">Value</th>
                          <th className="p-2 text-left">Last Sold</th>
                          <th className="p-2 text-left">Last Recv</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.products.map((p, i) => (
                          <tr
                            key={p.id}
                            className={i % 2 ? "bg-gray-50" : "bg-white"}
                            data-testid={`cleanup-row-${i}`}
                          >
                            <td className="p-2">
                              <Checkbox
                                checked={selectedIds.has(p.id)}
                                onCheckedChange={() => toggleOne(p.id)}
                              />
                            </td>
                            <td className="p-2 font-mono">{p.plu_code}</td>
                            <td className="p-2">{p.name}</td>
                            <td className="p-2 text-gray-600">{p.supplier_name || "—"}</td>
                            <td className="p-2 text-right">{p.current_stock}</td>
                            <td className="p-2 text-right">{formatCurrency(p.stock_value)}</td>
                            <td className="p-2 text-gray-600">{formatDate(p.last_sold_at)}</td>
                            <td className="p-2 text-gray-600">{formatDate(p.last_received_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>

        {/* Hard-delete typed confirmation */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Permanent delete
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p>
                You're about to <strong>permanently delete {selectedIds.size} product(s)</strong>.
                This cannot be undone — past sales references to these products will go stale in
                some reports.
              </p>
              <p className="text-amber-700 font-medium">
                Recommended: use <em>Archive</em> instead — it hides them from every screen but
                keeps the data so reports stay intact.
              </p>
              <Label className="text-xs">
                Type <code className="bg-gray-100 px-1">DELETE FOREVER</code> to confirm
              </Label>
              <Input
                value={deletePhrase}
                onChange={(e) => setDeletePhrase(e.target.value)}
                placeholder="DELETE FOREVER"
                data-testid="cleanup-delete-confirm-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeletePhrase(""); }}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deletePhrase.trim().toUpperCase() !== "DELETE FOREVER" || deleting}
                onClick={hardDelete}
                data-testid="cleanup-confirm-delete-btn"
              >
                {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                Delete {selectedIds.size} product(s) permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default UnusedProductsCleanup;
